import { createModalA11y } from '@/js/utils/modal-a11y';

(() => {
  const P = {
    /* =========================
     * Helpers
     * ========================= */
    qs(sel, root = document) {
      return root.querySelector(sel);
    },
    qsa(sel, root = document) {
      return Array.from(root.querySelectorAll(sel));
    },

    show(el) {
      if (!el) return;

      el.style.display = "block";

      if (el.getAttribute("role") !== "dialog") return;

      this.activeModalA11y?.deactivate();

      this.activeModalA11y = createModalA11y(el, {
        initialFocus: () => el.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        ) || el,
      });

      this.activeModalA11y?.activate();
    },
    hide(el) {
      if (!el) return;

      el.style.display = "none";

      if (el.getAttribute("role") === "dialog") {
        this.activeModalA11y?.deactivate();
        this.activeModalA11y = null;
      }
    },
    after(refEl, htmlString) {
      if (!refEl) return;
      refEl.insertAdjacentHTML("afterend", htmlString || "");
    },

    getCustomerId() {
      return this.qs("#customer_shopify_id")?.value ?? "";
    },

    returnShopNameForHeader() {
      return { "Access-Hash": window.DiiaConf?.customer_hash };
    },
    returnDiiaShopName() {
      return window.DiiaConf?.diia_deeplink_api_domain;
    },
    returnShopName() {
      return window.DiiaConf?.diia_api_domain;
    },

    /* =========================
     * Popups accessors
     * ========================= */
    getInfoPopup() {
      return this.qs("[data-info-popup]");
    },

    getDiaInfoPopup() {
      return this.qs("[data-dia-info]");
    },

    getPopupAlready() {
      return this.qs("[data-popup-already]");
    },

    /* =========================
     * Lifecycle
     * ========================= */
    init() {
      this.checkAgeVerification();
      this.initPage();
      this.bindPopupHandlers();
    },

    initPage() {
      this.renderMainVerificationPage();

      setTimeout(() => {
        this.updateUrl();
        this.startTimer();
      }, 1000);
    },

    renderMainVerificationPage() {
      const host = this.qs(".verification-v2_block");
      const tpl = this.qs("#mainVerifSection");
      this.after(host, tpl?.innerHTML ?? "");
    },

    /* =========================
     * Popup logic
     * ========================= */
    bindPopupHandlers() {
      if (this._popupHandlersBound) return;
      this._popupHandlersBound = true;

      document.addEventListener("click", (e) => {
        /* ===== OPENERS ===== */
        if (e.target.closest(".main-verification-page__skip")) {
          this.show(this.getInfoPopup());
          return;
        }

        if (e.target.closest("[data-dia-info-opener]")) {
          this.show(this.getDiaInfoPopup());
          return;
        }

        /* ===== CLOSE BUTTONS ===== */
        if (e.target.closest("[data-info-popup] .close_modal")) {
          this.hide(this.getInfoPopup());
          return;
        }

        if (e.target.closest("[data-dia-info] .close_modal")) {
          this.hide(this.getDiaInfoPopup());
          return;
        }

        if (e.target.closest("[data-popup-already] .close_modal")) {
          this.hide(this.getPopupAlready());
          return;
        }
      });

      document.addEventListener("mouseup", (e) => {
        /* ===== INFO POPUP ===== */
        this.handleOutsideClick(
          e,
          this.getInfoPopup(),
          "[data-info-popup] .info_popup_block_cont"
        );

        /* ===== DIA INFO POPUP ===== */
        this.handleOutsideClick(
          e,
          this.getDiaInfoPopup(),
          "[data-dia-info] .info_popup_block_cont"
        );

        /* ===== DUPLICATED POPUP ===== */
        this.handleOutsideClick(
          e,
          this.getPopupAlready(),
          "[data-popup-already] .info_popup_block_cont"
        );
      });

      document.addEventListener("keydown", (e) => {
        if (e.key !== "Escape") return;

        const activePopup = [
          this.getInfoPopup(),
          this.getDiaInfoPopup(),
          this.getPopupAlready(),
        ].find((popup) => popup && window.getComputedStyle(popup).display !== "none");

        if (activePopup) this.hide(activePopup);
      });
    },

    handleOutsideClick(e, popup, contentSelector) {
      const cont = this.qs(contentSelector);
      if (!popup || !cont) return;

      const clickedInside =
        cont === e.target || cont.contains(e.target);

      if (!clickedInside) this.hide(popup);
    },

    /* =========================
     * Deeplink / QR fetch
     * ========================= */
    async updateUrl() {
      await this.getAgeVerification();
    },

    async getAgeVerification() {
      const domain = this.returnDiiaShopName();
      if (!domain) return;

      try {
        const res = await fetch(
          `https://${domain}/apps/diia/deeplink`,
          {
            method: "POST",
            headers: {
              ...this.returnShopNameForHeader(),
              Accept: "application/json",
              "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
            },
            body: new URLSearchParams({
              customer_id: this.getCustomerId(),
            }),
          }
        );

        const data = await res.json();

        this.show(this.qs(".verification-profile-block"));

        if (data?.data?.deeplink) {
          this.qs(".verification_btn")?.setAttribute(
            "href",
            `https://diia.page.link?link=${data.data.deeplink}`
          );
        }

        if (data?.data?.deeplink_qr) {
          this.qs(".qr-code")?.setAttribute(
            "src",
            `data:image/png;base64, ${data.data.deeplink_qr}`
          );
        }
      } catch (err) {
        console.error("getAgeVerification error:", err);
      }
    },

    /* =========================
     * Timer
     * ========================= */
    startTimer() {
      if (this._timerId) return;
      this.tickTimer();
    },

    tickTimer() {
      const el = this.qs("#timer");
      if (!el) return;

      let [m, s] = el.textContent.split(":").map(Number);

      s--;
      if (s < 0) {
        s = 59;
        m--;
      }

      if (isNaN(m) || m < 0) m = 2;

      el.textContent =
        `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;

      this._timerId = setTimeout(() => this.tickTimer(), 1000);
    },

    /* =========================
     * Verification polling
     * ========================= */
    checkAgeVerification() {
      if (this._verifyIntervalId) return;

      this._verifyIntervalId = setInterval(
        () => this.pollVerification(),
        5000
      );
    },

    async pollVerification() {
      const domain = this.returnShopName();
      if (!domain) return;

      try {
        const url = new URL(
          `https://${domain}/api/customer/is-verified`
        );
        url.searchParams.set("customer_id", this.getCustomerId());

        const res = await fetch(url, {
          headers: { Accept: "application/json" },
        });

        const data = await res.json();

        if (data?.duplicated === true) {
          this.show(this.getPopupAlready());
          clearInterval(this._verifyIntervalId);
          this._verifyIntervalId = null;
          return;
        }
      } catch (err) {
        console.error("pollVerification error:", err);
      }
    },
  };

  document.addEventListener("DOMContentLoaded", () => P.init());
})();
